import { useState } from 'react'
import { UserPlus, Loader2, X, Fingerprint } from 'lucide-react'
import './GuestBanner.css'

interface GuestBannerProps {
    onCreateProfile?: () => Promise<void> | void
    /** When false, hides self-signup entry points. */
    signupEnabled?: boolean
    /** Optional: let returning users recover identity from server via passkey. */
    onRecoverFromServer?: () => Promise<boolean>
}

/**
 * Non-blocking top banner shown to guest users viewing shared workspaces.
 * Gently suggests creating a profile without gating workspace access.
 */
export default function GuestBanner({ onCreateProfile, signupEnabled = false, onRecoverFromServer }: GuestBannerProps) {
    const [dismissed, setDismissed] = useState(false)
    const [creating, setCreating] = useState(false)
    const [recovering, setRecovering] = useState(false)

    if (dismissed) return null

    const busy = creating || recovering

    async function handleCreate() {
        if (!signupEnabled || !onCreateProfile) return
        setCreating(true)
        try {
            await onCreateProfile()
        } finally {
            setCreating(false)
        }
    }

    async function handleRecover() {
        if (!onRecoverFromServer) return
        setRecovering(true)
        try {
            await onRecoverFromServer()
        } finally {
            setRecovering(false)
        }
    }

    return (
        <div className="guest-banner" role="status">
            <span className="guest-banner-text">
                👋 You&apos;re browsing as a guest.
            </span>
            {onRecoverFromServer && (
                <button
                    className="guest-banner-action guest-banner-action--sign-in"
                    onClick={handleRecover}
                    disabled={busy}
                >
                    {recovering
                        ? <><Loader2 size={14} className="spin" /> Signing in…</>
                        : <><Fingerprint size={14} /> Sign in</>
                    }
                </button>
            )}
            {signupEnabled && onCreateProfile && (
                <button
                    className="guest-banner-action"
                    onClick={handleCreate}
                    disabled={busy}
                >
                    {creating
                        ? <><Loader2 size={14} className="spin" /> Creating…</>
                        : <><UserPlus size={14} /> Create profile</>
                    }
                </button>
            )}
            <button
                className="guest-banner-dismiss"
                onClick={() => setDismissed(true)}
                aria-label="Dismiss"
            >
                <X size={14} />
            </button>
        </div>
    )
}
